import React from 'react';
import { BellOutlined, UserOutlined, WarningOutlined, InfoCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import type { Notification, NotificationType } from '../../types/notification.types';
import { useAppStore } from '../../stores/useAppStore';

interface NotificationItemProps {
  notification: Notification;
  compact?: boolean;
  onClose?: () => void;
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'data-update':
      return <UserOutlined />;
    case 'system':
      return <CheckCircleOutlined />;
    case 'error':
      return <WarningOutlined />;
    case 'business':
      return <InfoCircleOutlined />;
    default:
      return <BellOutlined />;
  }
};

const getRelativeTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин. назад`;
  if (hours < 24) return `${hours} ч. назад`;
  if (days < 7) return `${days} д. назад`;
  
  return new Date(date).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
  });
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  compact = false,
  onClose,
}) => {
  const navigate = useNavigate();
  const markAsRead = useAppStore((state) => state.markAsRead);
  const deleteNotification = useAppStore((state) => state.deleteNotification);

  const handleClick = () => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    if (notification.link) {
      navigate(notification.link.path);
      onClose?.();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNotification(notification.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className={`notification-item ${notification.read ? 'read' : 'unread'} ${compact ? 'compact' : ''}`}
      onClick={handleClick}
      style={{ cursor: notification.link ? 'pointer' : 'default' }}
    >
      <div className={`notification-icon notification-icon--${notification.type}`}>
        {getNotificationIcon(notification.type)}
      </div>

      <div className="notification-content">
        <div className="notification-header">
          <h4 className="notification-title">{notification.title}</h4>
          <span className="notification-time">{getRelativeTime(notification.timestamp)}</span>
        </div>
        
        <p className="notification-message">{notification.message}</p>
        
        {notification.link && !compact && (
          <span className="notification-link">{notification.link.label}</span>
        )}
      </div>

      <button
        className="notification-delete"
        onClick={handleDelete}
        aria-label="Удалить уведомление"
      >
        ×
      </button>

      {!notification.read && <div className="notification-unread-indicator" />}
    </motion.div>
  );
};


